import React, {useEffect, useState} from "react";
import {Box, Grid, InputAdornment, Paper, Typography} from '@material-ui/core';
import {useForm} from "react-hook-form";
import Button from "@material-ui/core/Button";
import addressData from '../../data/addressData.json'
import axiosInstance from "../../api";
import Spinner from "../../components/Spinner";
import {useConsumerContext} from "../../context/ConsumerContext";
import {AVAILABLE_ACTIONS} from "../../utils/constants";
import {MUIAutoComplete, MUIConfirmationDialog, MUIInput, MUISwitch} from "../../utils/formUtils";
import * as yup from "yup";
import {yupResolver} from "@hookform/resolvers/yup";
import {Alert} from "@material-ui/lab";
import {makeStyles} from "@material-ui/styles";
import GoogleMaps from "../../utils/GooglePlacesInput";
import {CloseSharp} from "@mui/icons-material";

const imgStyle = makeStyles({
    root: {
        backgroundColor: '#007DCB'
    }
})

const validationSchema = yup.object().shape({
    addressLine1: yup.string().when('isAddressStandardized', {
        is: (isAddressStandardized) => isAddressStandardized === false,
        then: yup.string().required('Address required.').nullable(),
        otherwise: yup.string().notRequired().nullable()
    }),
    city: yup.string().when('isAddressStandardized', {
        is: (isAddressStandardized) => isAddressStandardized === false,
        then: yup.string().required('City required.').nullable(),
        otherwise: yup.string().notRequired().nullable()
    }),
    county: yup.string().when('isAddressStandardized', {
        is: (isAddressStandardized) => isAddressStandardized === false,
        then: yup.string().required('County required.').nullable(),
        otherwise: yup.string().nullable()
    }),
    zip: yup.number().typeError('Zip must be a valid Florida code').when('isAddressStandardized', {
        is: (isAddressStandardized) => isAddressStandardized === false,
        then: yup.number().typeError('Zip must be a valid Florida code').required('Florida ZIP required.').positive('Zip must be a valid Florida code').integer().min(32003, 'Zip must be a valid Florida code').max(34997, 'Zip must be a valid Florida code'),
        otherwise: yup.number().nullable()
    })
});
export default function AddressPickerGP() {
    // triggering the react hook form
    const {
        handleSubmit,
        watch,
        control,
        reset,
        formState: {errors}
    } = useForm({resolver: yupResolver(validationSchema)});

    // context from parent
    const {consumerData, dispatch} = useConsumerContext();
    const watchValues = watch();

    React.useEffect(() => {
        console.log('Watched Values:',watchValues );
        console.log('Form Errors:', errors);
    }, [watchValues, errors]);
    // Image style
    const classes = imgStyle();

    // loading static data when component loads
    useEffect(() => {
        // Anything in here is fired on component mount.
        // fetching static data
        setCityList(addressData.city);
        setCountyList(addressData.county);
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"userRequestStatus": "INIT"}});
        // loading csrf as cookie so tht it can be used in next post
        axiosInstance.get('unauth/leads/csrf', {withCredentials: true})
    }, []);

    // state variables
    const [cityList, setCityList] = useState([]);
    const [countyList, setCountyList] = useState([]);
    const [showSpinner, setShowSpinner] = useState(false);
    const [hasError, setHasError] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [svcResponse, setSvcResponse] = useState(null);

    // form submission
    const onSubmit = data => {
        setShowSpinner(true);
        data["grecaptcha"] = consumerData?.grecaptcha;
        if ((consumerData?.userRequestId && "init" != consumerData.userRequestId) || (svcResponse && svcResponse.userRequestId != null)) {
            updateUserRequest();
        } else {
            createUserRequest(data);
        }

    }

    const getAddressObject = (address_components) => {
        const ShouldBeComponent = {
            home: ["street_number"],
            postal_code: ["postal_code"],
            street: ["street_address", "route"],
            region: [
                "administrative_area_level_1",
                "administrative_area_level_2",
                "administrative_area_level_3",
                "administrative_area_level_4",
                "administrative_area_level_5"
            ],
            city: [
                "locality",
                "neighborhood",
                "sublocality",
                "sublocality_level_1",
                "sublocality_level_2",
                "sublocality_level_3",
                "sublocality_level_4"
            ],
            country: ["country"],
            county: [
                "administrative_area_level_2",
                "political"
            ]
        };

        const address = {
            home: "",
            postal_code: "",
            street: "",
            region: "",
            city: "",
            country: "",
            county: ""
        };
        address_components.forEach(component => {
            for (let shouldBe in ShouldBeComponent) {
                if (ShouldBeComponent[shouldBe].indexOf(component.types[0]) !== -1) {
                    if (shouldBe === "country") {
                        address[shouldBe] = component.short_name;
                    } else {
                        address[shouldBe] = component.long_name;
                    }
                }
            }
        });
        return address;
    }

    const populateValues = (data) => {
        //console.log(data[0].address_components)
        reset();
        if( data && data[0] && data[0].address_components){
            const address =  getAddressObject(data[0].address_components)
            //console.log('i will populate values here...', address)
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"addressLine1": address.home + ' ' +address.street}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"city": address.city}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"zip": address.postal_code}});
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"isAddressStandardized": true}});
            handleCountyConversion( address.county);
        }
    }

    const handleCountyConversion = (countyValue) => {
        if (countyValue) {
            countyValue = countyValue.replace(' County', '');
            if (countyValue.includes('St. Lucie') || countyValue.includes('Saint Lucie')  || countyValue.includes('St Lucie')) {
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": 'Saint Lucie'}});
            } else if (countyValue.includes('Saint Johns') || countyValue.includes('St. Johns')  || countyValue.includes('St Johns')) {
                dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": 'Saint Johns'}});
            } else {
                let countyFromList = countyList.filter(z => z=== countyValue);
                if (countyFromList && countyFromList.length > 0) {
                    dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": countyFromList[0]}});
                }
            }
        }
    }

    const resetValues = () => {
            reset();
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"addressLine1": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"city": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"zip": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"county": ''}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"isAddressStandardized": false}});

    }

    function updateUserRequest() {
        setSvcResponse(null);
        let payload = Object.assign({}, consumerData);
        delete payload.roof;
        delete payload.electrical;
        delete payload.plumbing;
        axiosInstance.post("unauth/leads/updateUserRequestInterm", payload).then(address => {
            setSvcResponse(Object.assign({},address));
            if (address?.duplicateRequest) {
                displayDuplicateWarning();
            } else {
                onUpdateUserRequest(address);
            }
        }).catch(e => {
            setHasError(true);
        }).finally(() => setShowSpinner(false));
    }

    function createUserRequest() {
        setSvcResponse(null);
        // todo : change the payload to data. data was getting messed up after the google places thing and with time constraint had to ....
        axiosInstance.post("unauth/leads/newUserRequest", {
            addressLine1:consumerData?.addressLine1,
            addressLine2:consumerData?.addressLine2,
            city:consumerData?.city,
            state:'FL',
            zip: consumerData?.zip,
            county: consumerData?.county,
            isAddressStandardized: consumerData?.isAddressStandardized,
            grecaptcha:consumerData?.grecaptcha
        }).then(address => {
            setSvcResponse(Object.assign({},address));
            if (address?.duplicateRequest) {
                onCreateSuccess(address);
                displayDuplicateWarning();
            } else {
                onCreateSuccess(address);
                dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
            }
        }).catch(e => {
            setHasError(true);
        }).finally(() => setShowSpinner(false));
    }

    function onCreateSuccess(address) {
        let addressValue = (address) ? address : svcResponse;
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: addressValue});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"insuranceReason": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"propertyUsage": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"propertyType": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"constructionType": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"numOfClaims": "-100"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"businessType": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"roof": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"electrical": "none"}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"plumbing": "none"}});
        if (addressValue && addressValue.distanceToCoastPrefill !== 'Y') {
            dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"milesFromSaltWater": "-100"}});
        }
        setHasError(false);
    }

    function onUpdateUserRequest(address) {
        let addressValue = (address) ? address : svcResponse;
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"fireStationPrefill": addressValue?.fireStationPrefill}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"distanceToCoastPrefill": addressValue?.distanceToCoastPrefill}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"milesFromSaltWater": addressValue?.milesFromSaltWater}});
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {"is5miFromMannedFireStation": addressValue?.is5miFromMannedFireStation}});
        dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
        setHasError(false);
    }

    function displayDuplicateWarning() {
        setOpenDialog(true);
    }

    function handleDClose() {
        setOpenDialog(false);
    }

    function handleDConfirm() {
        setOpenDialog(false);
        if (consumerData?.userRequestId && "init" != consumerData.userRequestId) {
            onUpdateUserRequest();
        } else {
            onCreateSuccess();
            dispatch({type: AVAILABLE_ACTIONS.MOVE_NEXT, wizardStep: consumerData.wizardStep + 1});
        }
    }

    // updating the state as the user types...
    function handleFormChange(event) {
        dispatch({type: AVAILABLE_ACTIONS.UPDATE_ADDRESS, userRequest: {[event.target.name]: event.target.value}});
    }

    // spinner
    if (showSpinner) return <Spinner/>;

    return (
        <section className="pageBackground">
            <div className="pageCenter">
                <Typography align="center" gutterBottom>
                    <p className="centerText"> Register a Property with <center className="lightBlue">FMAP</center></p>
                </Typography>
                {consumerData.isAddressStandardized &&
                    (<form className="pageForm"
                           onSubmit={handleSubmit(onSubmit)}>
                            <Paper style={{padding: 24}}>
                                <Grid container alignItems="flex-start" spacing={2}>
                                       <span style={{display: "none"}}>
                                    <MUISwitch errors={errors} defaultValue={consumerData?.isAddressStandardized}
                                               value={consumerData?.isAddressStandardized} name="isAddressStandardized"
                                               control={control} onChecked={handleFormChange}/>
                                </span>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <MUIInput control={control} name="addressLine1" inputPrp={{maxLength: 200}}
                                                  iconProperties={{
                                                      endAdornment: (
                                                          <InputAdornment position="start">
                                                              <CloseSharp onClick={() => resetValues()}/>
                                                          </InputAdornment>
                                                      )
                                                  }}
                                                  disabled={consumerData?.isAddressStandardized}
                                                  defaultValue={consumerData?.addressLine1}
                                                  value={consumerData?.addressLine1} label="Address Line 1"
                                                  onInputChange={handleFormChange} errors={errors}/>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <MUIInput control={control} name="addressLine2" inputPrp={{maxLength: 200}}
                                                  defaultValue={consumerData?.addressLine2}
                                                  value={consumerData?.addressLine2} label="Address Line 2"
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>

                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIInput control={control} name="city" defaultValue={consumerData?.city}
                                                  value={consumerData?.city} label="City"
                                                  error={consumerData?.errors?.city} disabled
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIAutoComplete id="county" control={control} name="county" label="County"
                                                         disabled
                                                         errors={errors}
                                                         value={consumerData?.county} onValueChange={handleFormChange}
                                                         options={countyList}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIInput control={control} name="zip" defaultValue={consumerData?.zip}
                                                  inputPrp={{maxLength: 5}}
                                                  disabled
                                                  value={consumerData?.zip} label="ZIP"
                                                  error={consumerData?.errors?.zip}
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box component="span">
                                            <Button type="submit" variant="contained" color="primary" name="btnNext"
                                                    className="searchBtn">
                                                Next
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} sm={12} md={12}>
                                        {hasError && <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">An error occurred. Please try again
                                                later.</Alert>
                                        </div>}
                                    </Grid>
                                </Grid>
                            </Paper>
                        </form>
                    )}
                {!consumerData.isAddressStandardized &&
                    (<form className="pageForm"
                           onSubmit={handleSubmit(onSubmit)}>
                            <Paper style={{padding: 24}}>
                                <Grid container alignItems="flex-start" spacing={2}>
                                       <span style={{display:"none"}}>
                                    <MUISwitch errors={errors} defaultValue={consumerData?.isAddressStandardized}
                                               value={consumerData?.isAddressStandardized} name="isAddressStandardized"
                                               control={control} onChecked={handleFormChange}/>
                                </span>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <GoogleMaps control={control} name="addressLine1" inputPrp={{maxLength: 200}}
                                                    onValuChange={(newValue) => {
                                                        dispatch({
                                                            type: AVAILABLE_ACTIONS.UPDATE_ADDRESS,
                                                            userRequest: {"addressLine1": newValue}
                                                        })
                                                    }} valueOnLoad={consumerData?.addressLine1} floridaOnly
                                                    label="Address Line 1" errors={errors}
                                                    callback={populateValues} resetCallback={resetValues}/>
                                    </Grid>
                                    <Grid item xs={12} sm={12} md={6}>
                                        <MUIInput control={control} name="addressLine2" inputPrp={{maxLength: 200}}
                                                  defaultValue={consumerData?.addressLine2}
                                                  value={consumerData?.addressLine2} label="Address Line 2"
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>

                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIAutoComplete id="city" control={control} name="city" label="City"
                                                         errors={errors}
                                                         value={consumerData?.city}
                                                         onValueChange={handleFormChange}
                                                         options={cityList}/>

                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIAutoComplete id="county" control={control} name="county" label="County"
                                                         errors={errors}
                                                         value={consumerData?.county} onValueChange={handleFormChange}
                                                         options={countyList}/>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <MUIInput control={control} name="zip" defaultValue={consumerData?.zip}
                                                  inputPrp={{maxLength: 5}}
                                                  value={consumerData?.zip} label="ZIP"
                                                  error={consumerData?.errors?.zip}
                                                  onInputChange={handleFormChange} errors={errors}></MUIInput>
                                    </Grid>
                                    <Grid item xs={12} sm={6} md={3}>
                                        <Box component="span">
                                            <Button type="submit" variant="contained" color="primary" name="btnNext"
                                                    className="searchBtn">
                                                Next
                                            </Button>
                                        </Box>
                                    </Grid>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} sm={12} md={12}>
                                        {hasError && <div>
                                            <br/>
                                            <Alert variant="filled" severity="error">An error occurred. Please try again
                                                later.</Alert>
                                        </div>}
                                    </Grid>
                                </Grid>
                            </Paper>
                        </form>
                    )}
            </div>
            <div className="homeBg">&nbsp;</div>
            <MUIConfirmationDialog dialogTitle="Existing Application?"
                                   dialogContent="An active property quote request already exists for this address. You can review your property quote request information and/or update the “need by” date for your request by selecting the link provided in your confirmation email or by using the “Search” button located on the Consumer page of the FMAP website. For additional assistance, please submit your question using Contact Us."
                                   showDialog={openDialog} handleDialogClose={handleDClose}
                                   handleUserConfirmation={handleDConfirm}></MUIConfirmationDialog>

        </section>
    );
}
